/* product-reviews-trautis.js
 * Custom Judge.me review renderer for the PDP "Bewertungen" accordion.
 *
 * Judge.me serves a stuck legacy widget on this store: its pagination is broken
 * (clicking page 2 does nothing; the legacy fetch 404s) and the "reviews per page"
 * setting that would put every review on one page is locked behind the paid Awesome
 * plan. So we render reviews ourselves from Judge.me's PUBLIC, no-auth, CORS-open
 * endpoint (api.judge.me/reviews/reviews_for_widget, per_page=100) — all reviews on
 * one page, no pagination needed.
 *
 * Review photos are intentionally NOT rendered (imported reviews link to a foreign
 * alicdn CDN — see prior decision). There is no "write a review" control by design.
 *
 * Markup hook (rendered by sections/product-content-trautis.liquid):
 *   <div class="t-bewertungen" data-trautis-reviews
 *        data-product-id="…" data-shop="….myshopify.com" data-count="N">…</div>
 * Only rendered when the product has reviews, so the fetch never fires on the
 * (majority) zero-review products.
 */
(function () {
  'use strict';

  var ENDPOINT = 'https://api.judge.me/reviews/reviews_for_widget';

  function esc(s) {
    return (s == null ? '' : String(s)).replace(/[&<>"]/g, function (c) {
      return { '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;' }[c];
    });
  }

  // Verifiziert-Siegel — identisch zur Karussell-/Streifen-Variante in
  // reviews-carousel-trautis.js (scalloped Rosette in currentColor + weißer Haken).
  // Bewusst dupliziert: beide Renderer sind eigenständige IIFEs ohne Build-Step.
  function verifiedSealSvg() {
    return '<svg class="t-seal" viewBox="0 0 24 24" width="15" height="15" aria-hidden="true" focusable="false">'
      + '<path fill="currentColor" d="M12 1 L14.28 3.5 L17.5 2.47 L18.22 5.78 L21.53 6.5 L20.5 9.72 L23 12 L20.5 14.28 L21.53 17.5 L18.22 18.22 L17.5 21.53 L14.28 20.5 L12 23 L9.72 20.5 L6.5 21.53 L5.78 18.22 L2.47 17.5 L3.5 14.28 L1 12 L3.5 9.72 L2.47 6.5 L5.78 5.78 L6.5 2.47 L9.72 3.5 Z"/>'
      + '<path fill="none" stroke="#fff" stroke-width="2.2" stroke-linecap="round" stroke-linejoin="round" d="M7.8 12.4 11 15.4 16.3 9.2"/>'
      + '</svg>';
  }
  function verifiedBadgeHtml(rev) {
    if (!rev || rev.verified_buyer !== true) return '';
    return '<span class="t-bewertungen__rev-badge">' + verifiedSealSvg() + 'Verifizierter Kauf</span>';
  }

  function starsHtml(rating, label) {
    var n = Math.round(rating || 0);
    var out = '<span class="t-bewertungen__stars"' + (label ? ' aria-label="' + esc(label) + '"' : '') + '>';
    for (var i = 1; i <= 5; i++) {
      out += '<span class="t-bewertungen__star' + (i <= n ? '' : ' t-bewertungen__star--off') + '" aria-hidden="true">★</span>';
    }
    return out + '</span>';
  }

  function fmtDate(iso) {
    try {
      return new Date(iso).toLocaleDateString('de-DE', { year: 'numeric', month: 'long', day: 'numeric' });
    } catch (e) { return ''; }
  }

  // Judge.me body_html is untrusted user text; render as plain text (preserving line
  // breaks), never as live HTML. Parse with DOMParser('text/html'), NOT div.innerHTML:
  // DOMParser builds an inert document that runs no scripts and loads no subresources, so
  // a review body cannot trigger image/pixel fetches (e.g. the imported-review alicdn
  // leak) or inject markup while we extract its text.
  function bodyHtml(html) {
    var s = (html || '').replace(/<br\s*\/?>/gi, '\n').replace(/<\/p>/gi, '\n');
    var doc = new DOMParser().parseFromString(s, 'text/html');
    var text = ((doc.body && doc.body.textContent) || '').trim();
    if (!text) return '';
    return esc(text).replace(/\n{2,}/g, '<br><br>').replace(/\n/g, '<br>');
  }

  // innerHTML is used below, but it is XSS-safe by construction: every value that
  // originates from the API (names, titles, bodies, dates, ratings, counts) is either
  // run through esc() or coerced to a Number before being concatenated. Review bodies
  // are reduced to plain text via bodyHtml() and only re-gain generated <br> tags — raw
  // source HTML is never inserted. Keep this invariant if you edit render().
  function render(el, data) {
    var reviews = (data && data.reviews) || [];
    if (!reviews.length) {
      el.innerHTML = '<p class="prose-trautis">Noch keine Bewertungen vorhanden.</p>';
      return;
    }
    var count = parseInt(data.number_of_reviews, 10);
    if (!(count >= 0)) count = reviews.length;
    var avg = data.average_rating ? parseFloat(data.average_rating) || 0 : 0;
    var avgStr = avg ? avg.toFixed(1).replace('.', ',') : '–';

    var html = '<div class="t-bewertungen__summary"><div class="t-bewertungen__rating-block">'
      + '<span class="t-bewertungen__rating-big">' + esc(avgStr) + '</span>'
      + starsHtml(avg, avgStr + ' von 5 Sternen')
      + '<span class="t-bewertungen__count">' + count + (count === 1 ? ' Bewertung' : ' Bewertungen') + '</span>'
      + '</div></div><ul class="t-bewertungen__list">';

    reviews.forEach(function (rev) {
      var name = (rev.is_anonymous_reviewer || !rev.reviewer_name) ? 'Anonym' : rev.reviewer_name;
      var verified = verifiedBadgeHtml(rev);
      var date = rev.created_at ? '<time class="t-bewertungen__rev-date">' + esc(fmtDate(rev.created_at)) + '</time>' : '';
      var title = rev.title ? '<p class="t-bewertungen__rev-title">' + esc(rev.title) + '</p>' : '';
      var body = bodyHtml(rev.body_html);
      html += '<li class="t-bewertungen__rev"><div class="t-bewertungen__rev-head">'
        + starsHtml(rev.rating, (rev.rating || 0) + ' von 5')
        + '<span class="t-bewertungen__rev-author">' + esc(name) + '</span>'
        + verified + date
        + '</div>'
        + title
        + (body ? '<div class="t-bewertungen__rev-body">' + body + '</div>' : '')
        + '</li>';
    });
    el.innerHTML = html + '</ul>';
  }

  function load(el) {
    if (el.__trautisReviewsLoaded) return;
    el.__trautisReviewsLoaded = true;
    var pid = el.getAttribute('data-product-id');
    var shop = el.getAttribute('data-shop');
    if (!pid || !shop) return;
    var url = ENDPOINT
      + '?url=' + encodeURIComponent(shop)
      + '&shop_domain=' + encodeURIComponent(shop)
      + '&platform=shopify&page=1&per_page=100&product_id=' + encodeURIComponent(pid);
    fetch(url, { headers: { Accept: 'application/json' } })
      .then(function (r) { if (!r.ok) throw new Error('HTTP ' + r.status); return r.json(); })
      .then(function (data) { render(el, data); })
      .catch(function () {
        el.innerHTML = '<p class="prose-trautis">Bewertungen konnten gerade nicht geladen werden. Bitte später erneut versuchen.</p>';
        el.__trautisReviewsLoaded = false; // allow a retry on next open
      });
  }

  function boot() {
    var nodes = document.querySelectorAll('[data-trautis-reviews]');
    Array.prototype.forEach.call(nodes, function (el) {
      var details = el.closest('details');
      if (!details || details.open) { load(el); return; }
      details.addEventListener('toggle', function () { if (details.open) load(el); });
    });
  }

  if (typeof module !== 'undefined' && module.exports) {
    module.exports = { esc: esc, verifiedSealSvg: verifiedSealSvg, verifiedBadgeHtml: verifiedBadgeHtml };
  }

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', boot);
  else boot();
})();
